import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ADMIN } from "@/constants/testIds";
import api from "@/lib/api";

export default function AdminLogin() {
  const { admin, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const navigate = useNavigate();

  if (admin) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); navigate("/admin"); }
    catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Accesso negato");
    } finally { setBusy(false); }
  };

  const requestReset = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await api.post("/auth/password-reset-request", { email: forgotEmail });
      toast.success("Se l'account esiste, ti abbiamo inviato un'email con le istruzioni.");
      setForgotOpen(false); setForgotEmail("");
    } catch { toast.error("Errore, riprova"); }
    finally { setForgotBusy(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-bone px-6" data-testid="page-admin-login">
      {!forgotOpen ? (
      <form onSubmit={onSubmit} className="w-full max-w-md bg-white border border-hair p-10">
        <div className="eyebrow">Area Riservata</div>
        <h1 className="font-serif text-4xl mt-3">Accedi</h1>
        <p className="text-taupe text-sm mt-3">Gestione portfolio, prenotazioni e messaggi.</p>
        <div className="mt-8 space-y-5">
          <div>
            <Label className="text-xs tracking-[0.14em] uppercase">Email</Label>
            <Input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid={ADMIN.loginEmail} className="rounded-none mt-2" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.14em] uppercase">Password</Label>
            <Input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} data-testid={ADMIN.loginPassword} className="rounded-none mt-2" />
          </div>
        </div>
        <button type="submit" disabled={busy} className="btn-ink w-full mt-8 disabled:opacity-60" data-testid={ADMIN.loginSubmit}>
          {busy ? "Accesso…" : "Entra"}
        </button>
        <div className="mt-6 text-center">
          <button type="button" onClick={()=>{ setForgotEmail(email); setForgotOpen(true); }} className="text-xs tracking-[0.14em] uppercase text-taupe hover:text-ink" data-testid="admin-forgot-password">
            Password dimenticata?
          </button>
        </div>
      </form>
      ) : (
      <form onSubmit={requestReset} className="w-full max-w-md bg-white border border-hair p-10" data-testid="admin-forgot-form">
        <div className="eyebrow">Reimposta password</div>
        <h1 className="font-serif text-3xl mt-3">Ti mandiamo un link sicuro.</h1>
        <p className="text-taupe text-sm mt-3">Inserisci l'email del tuo account amministratore. Riceverai un'email con un link valido 2 ore per impostare una nuova password.</p>
        <div className="mt-8">
          <Label className="text-xs tracking-[0.14em] uppercase">Email</Label>
          <Input type="email" required value={forgotEmail} onChange={(e)=>setForgotEmail(e.target.value)} className="rounded-none mt-2" data-testid="admin-forgot-email" />
        </div>
        <button type="submit" disabled={forgotBusy} className="btn-ink w-full mt-8 disabled:opacity-60" data-testid="admin-forgot-submit">
          {forgotBusy ? "Invio…" : "Invia link di reset"}
        </button>
        <div className="mt-6 text-center">
          <button type="button" onClick={()=>setForgotOpen(false)} className="text-xs tracking-[0.14em] uppercase text-taupe hover:text-ink">← Torna al login</button>
        </div>
      </form>
      )}
    </div>
  );
}
